import { get, isEmpty, merge, omit } from 'lodash';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import { GO_GUEST_ID } from 'Containers/App/constants';
import { makeSelectConfigField, makeSelectJvpField } from 'Containers/App/selectors';
import { getCookie } from 'Containers/App/utils';
import {
  cancelDebouncedHouseholdUpdate,
  requestDebouncedHouseholdUpdate,
} from 'Containers/ProfilePage/actions';
import { PROFILE_SECTIONS } from 'Containers/ProfilePage/constants';
import {
  makeGetSelectedEnrollmentEventId,
  makeGetCanBearChild,
  makeGetIntegratedUserPublicationKey,
  makeGetSelectedPublicationKey,
  makeSelectProfileField,
  makeSelectShowClientSurvey,
  makeSelectSurvey,
  makeSelectIsClaimsFeatureEnabled,
  makeSelectHsaFundsOopCost,
} from 'Containers/ProfilePage/selectors';
import {
  ClaimsSummary,
  ClaimsFormStatus,
  IncentiveSurveyQuestion,
  NetworkPreferenceSurveyAnswers,
  NetworkPreferenceSurveyQuestion,
  ProfileSection,
  Survey,
} from 'Containers/ProfilePage/types';
import { JVPBootstrapPublicationInfo } from 'Utils/apiTypes';
import { checkFormIsComplete } from 'Utils/context';
import { sendInsightsEvent } from 'Utils/insights';

import { useFeatureFlagContext } from './featureFlagContext';

export interface ProfileContextType {
  profileProgressMap: ProgressMap;
  completedSections?: ProfileSection[];
  getSectionFromMap: (key: ProfileSection) => ProgressSection | null;
  getIsUnlocked: (key: ProfileSection) => boolean;
  getIsDirty: (key: ProfileSection) => boolean;
  getIsFormValid: () => boolean;
  getIsFormCompleted: () => boolean;
  markNotificationViewed: (key: ProfileSection) => void;
  handleUpdateProgress: ({
    key,
    isValid,
    isDirty,
    isComplete,
    ctaNotification,
  }: {
    key: ProfileSection;
    isValid: boolean;
    isDirty: boolean;
    isComplete: boolean;
    ctaNotification: CtaNotification | null;
  }) => void;
  handleSectionChange: ({
    key,
    isValid,
    isComplete,
  }: {
    key: ProfileSection;
    isValid: boolean;
    isComplete: boolean;
  }) => void;
  handleHiddenEligibility: () => void;
  selectedPublicationInfo: JVPBootstrapPublicationInfo | undefined;
  reviewMode: boolean;
  setReviewMode: (val: boolean) => void;
  tableViewEnabled: boolean;
  setTableViewEnabled: (val: boolean) => void;
}

export const useProfileContext = () => useContext(ProfileContext);

export const ProfileContext = React.createContext<ProfileContextType>({
  profileProgressMap: {},
  completedSections: [],
  selectedPublicationInfo: undefined,
  getSectionFromMap: () => null,
  getIsUnlocked: () => false,
  getIsDirty: () => false,
  getIsFormValid: () => false,
  getIsFormCompleted: () => false,
  markNotificationViewed: () => null,
  handleUpdateProgress: () => ({}),
  handleSectionChange: () => null,
  handleHiddenEligibility: () => null,
  reviewMode: false,
  setReviewMode: () => false,
  tableViewEnabled: true,
  setTableViewEnabled: () => true,
});

export enum CtaNotification {
  Error = 'Error',
  Success = 'Success',
}
export interface ProgressSection {
  isUnlocked: boolean;
  isValid: boolean;
  isDirty: boolean;
  isDisabled: boolean;
  isComplete: boolean;
  ctaNotification: CtaNotification | null;
}

export type ProgressMap = Record<ProfileSection, ProgressSection> | Record<string, never>;

interface ProfileProviderDispatchProps {
  requestDebouncedHouseholdUpdate: () => void;
  cancelDebouncedHouseholdUpdate?: () => void;
}

interface ProfileProviderParentProps {
  children: JSX.Element;
  progressMap?: ProgressMap; // Currently only used for passing in testing overrides
  completed?: ProfileSection[];
  isIntegrationPreFillEnabled: boolean;
}

interface ProfileProviderStoreProps {
  showClientSurvey: boolean;
  canBearChild: boolean;
  hidePregnancyQuestion: boolean;
  survey: Survey;
  activePublicationInfo: JVPBootstrapPublicationInfo | null;
  upcomingPublicationInfo: JVPBootstrapPublicationInfo | null;
  incentiveSurvey: IncentiveSurveyQuestion[];
  networkPreferenceSurvey: NetworkPreferenceSurveyQuestion[];
  selectedPublicationKey: 'active' | 'upcoming';
  incentiveSurveyAPICalled: boolean;
  networkPreferenceSurveyAPICalled?: boolean;
  networkPreferenceSurveyAnswers?: NetworkPreferenceSurveyAnswers;
  enrollmentEventId?: string;
  integratedUserPublicationKey: 'active' | 'upcoming' | null;
  claimsSummary: ClaimsSummary | null;
  claimsError: Error | null;
  claimsFormStatus: ClaimsFormStatus;
  isClaimsFeatureEnabled: boolean;
  hasOopCost: boolean;
  householdDataChanged: boolean;
}

export type ProfileProviderProps = ProfileProviderParentProps &
  ProfileProviderStoreProps &
  ProfileProviderDispatchProps;

export const ProfileProvider = ({
  canBearChild,
  hidePregnancyQuestion,
  showClientSurvey,
  survey,
  children,
  progressMap = {},
  completed = [],
  isIntegrationPreFillEnabled,
  activePublicationInfo,
  upcomingPublicationInfo,
  incentiveSurvey = [],
  networkPreferenceSurvey = [],
  selectedPublicationKey,
  incentiveSurveyAPICalled,
  networkPreferenceSurveyAPICalled = false,
  networkPreferenceSurveyAnswers = {},
  enrollmentEventId,
  integratedUserPublicationKey,
  isClaimsFeatureEnabled,
  claimsSummary,
  claimsFormStatus,
  claimsError,
  hasOopCost,
  householdDataChanged,
  requestDebouncedHouseholdUpdate,
  cancelDebouncedHouseholdUpdate,
}: ProfileProviderProps) => {
  const [profileProgressMap, setProfileProgressMap] = useState<ProgressMap>(progressMap);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [completedSections, setCompletedSections] = useState<ProfileSection[]>(completed);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [showIncentives, setShowIncentives] = useState(false);
  const [showProviderPreferences, setShowProviderPreferences] = useState(false);
  const [reviewMode, setReviewMode] = useState<boolean>(false); // State to manage reviewMode
  const [tableViewEnabled, setTableViewEnabled] = useState<boolean>(true); // State to manage reviewMode

  const { is_profile_prefill_enabled } = useFeatureFlagContext();
  const { is_hsa_funds_question_enabled } = useFeatureFlagContext();

  const [publicationInfo, setPublicationInfo] = useState<JVPBootstrapPublicationInfo | undefined>(undefined);

  const [notificationShown, setNotificationShown] = useState<boolean>(false);
  const oldClaimsFormStatus = useRef<ClaimsFormStatus | null>(null);
  const oldNotificationShown = useRef(false);
  const oldClaimsError = useRef<Error | null>(null);
  // Ensure previously visited sections are notified
  // when Claims content becomes available
  useEffect(() => {
    // we only want to perform this effect if claimsFormStatus, claimsError or the notificationShown state have changed.
    // Other pieces of state we are subscribed to are just needed for correct updates, but are not reasons to trigger
    // this effect.
    if (
      oldClaimsFormStatus.current === claimsFormStatus &&
      oldNotificationShown.current === notificationShown &&
      oldClaimsError.current === claimsError
    ) {
      return;
    }
    oldClaimsFormStatus.current = claimsFormStatus;
    oldNotificationShown.current = notificationShown;
    oldClaimsError.current = claimsError;

    let notificationForClaims: CtaNotification | null = null;
    let notificationForMember: CtaNotification | null = null;
    let notificationForRiskAssessment: CtaNotification | null = null;

    function sectionIsNotifiable(section) {
      return section.isUnlocked && (section.isDirty || section.isComplete);
    }

    if (claimsFormStatus !== ClaimsFormStatus.Skipped) {
      if (claimsError) {
        if (sectionIsNotifiable(profileProgressMap.riskAssessment)) {
          notificationForRiskAssessment = CtaNotification.Error;
        } else if (sectionIsNotifiable(profileProgressMap.member)) {
          notificationForMember = CtaNotification.Error;
        } else {
          notificationForClaims = CtaNotification.Error;
        }
      } else if (claimsSummary) {
        if (sectionIsNotifiable(profileProgressMap.riskAssessment)) {
          notificationForRiskAssessment = CtaNotification.Success;
        }
        if (sectionIsNotifiable(profileProgressMap.member)) {
          notificationForMember = CtaNotification.Success;
        }
      }
    }
    const shouldShowNotification = Boolean(
      notificationForClaims || notificationForMember || notificationForRiskAssessment,
    );

    let updatedProgressMap: ProgressMap = { ...profileProgressMap };
    let updated = false;

    const updateNotification = (
      section: ProfileSection,
      desiredctaNotification: CtaNotification | null,
    ): boolean => {
      const sec = profileProgressMap[section];
      if (sec.ctaNotification !== desiredctaNotification) {
        updatedProgressMap = {
          ...updatedProgressMap,
          [section]: { ...sec, ctaNotification: desiredctaNotification },
        } as ProgressMap;
        return true;
      }
      return false;
    };

    if (shouldShowNotification !== notificationShown) {
      updated = updateNotification('claims', notificationForClaims) || updated;
      updated = updateNotification('member', notificationForMember) || updated;
      updated = updateNotification('riskAssessment', notificationForRiskAssessment) || updated;
      if (updated) {
        setProfileProgressMap({ ...updatedProgressMap });
      }
      setNotificationShown(shouldShowNotification);
    }
  }, [claimsSummary, claimsError, claimsFormStatus, notificationShown, profileProgressMap]);

  useEffect(() => {
    // Hide enrollment question if:
    //  1. There is no active publication, or
    //  2. we've determined the publication from the AAPI user
    setShowEnrollment(!!activePublicationInfo && !integratedUserPublicationKey);
  }, [activePublicationInfo, upcomingPublicationInfo]);

  useEffect(() => {
    if (selectedPublicationKey === 'active' && activePublicationInfo) {
      setPublicationInfo(activePublicationInfo);
    }

    if (selectedPublicationKey === 'upcoming' && upcomingPublicationInfo) {
      setPublicationInfo(upcomingPublicationInfo);
    }
  }, [selectedPublicationKey, activePublicationInfo, upcomingPublicationInfo]);

  useEffect(() => {
    if (incentiveSurvey.length > 0) {
      setShowIncentives(true);
    } else {
      setShowIncentives(false);
    }
  }, [incentiveSurvey]);

  // An empty list is how the back end says "do not ask these", so a disabled section
  // must never block the form.
  useEffect(() => {
    setShowProviderPreferences(networkPreferenceSurvey.length > 0);
  }, [networkPreferenceSurvey]);

  useEffect(() => {
    // Filter out 'claims' section if claims data feature is disabled
    const filteredSections = isClaimsFeatureEnabled
      ? PROFILE_SECTIONS
      : PROFILE_SECTIONS.filter((section) => section !== 'claims');
    const initialState = filteredSections.reduce((acc, key) => {
      const initialValue = profileProgressMap[key] ||
        progressMap[key] || {
          isValid: false,
          isDirty: false,
          isDisabled: false,
          isComplete: false,
          ctaNotification: null,
        };
      return {
        ...acc,
        [key]: {
          ...initialValue,
          // Update to show eligibility as initial state if enrollment is disabled
          isUnlocked: showEnrollment ? key === 'enrollment' : key === 'eligibility',
          isDisabled: checkDisabled(key as ProfileSection),
        },
      };
    }, {}) as ProgressMap;
    Object.keys(initialState).forEach((section) => {
      initialState[section].isUnlocked = checkUnlocked(section as ProfileSection, initialState);
    });
    setProfileProgressMap(initialState);
  }, [showEnrollment, isClaimsFeatureEnabled, showClientSurvey]);

  useEffect(() => {
    if (!isClaimsFeatureEnabled) return;

    const isComplete =
      claimsFormStatus === ClaimsFormStatus.Skipped ||
      claimsFormStatus === ClaimsFormStatus.Submitted ||
      claimsFormStatus === ClaimsFormStatus.Success;

    if (isComplete) {
      handleSectionChange({
        key: 'claims',
        isValid: true,
        isComplete: true,
      });
    }
  }, [isClaimsFeatureEnabled, claimsFormStatus]);

  const profileCompleteInsightsEvent = () => {
    const goGuestId = getCookie(GO_GUEST_ID);
    sendInsightsEvent(null, 'profile_complete', { go_guest_id: goGuestId });
    setIsFormComplete(true);
  };
  // Add `setReviewMode` function
  const handleReviewMode = (val: boolean) => {
    setReviewMode(val);
  };

  // Add `tableViewEnabled` function
  const handleTableViewEnabled = (val: boolean) => setTableViewEnabled(val);

  useEffect(() => {
    // empty check to prevent this logic from running on first tick, before content is initialized
    if (!isEmpty(profileProgressMap)) {
      // Update continue btn state when all sections are valid for the first time
      if (
        !isFormComplete &&
        // Both surveys must have answered. A section whose questions have not arrived
        // is still disabled, so it counts as valid — without this the profile declares
        // itself complete in that gap and un-completes when the questions land.
        ((incentiveSurveyAPICalled && networkPreferenceSurveyAPICalled && getIsFormValid()) ||
          checkFormIsComplete(enrollmentEventId, profileProgressMap))
      ) {
        profileCompleteInsightsEvent();
      }

      // If client survey disabled, set eligibility to valid
      if (!showClientSurvey && !profileProgressMap.eligibility.isDisabled) {
        handleHiddenEligibility();
      }
    }
  }, [profileProgressMap]);

  useEffect(() => {
    // empty check to prevent this logic from running on first tick, before content is initialized
    if (!isEmpty(profileProgressMap)) {
      // Update continue btn state when all sections are valid for the first time.
      if (
        incentiveSurveyAPICalled &&
        networkPreferenceSurveyAPICalled &&
        incentiveSurvey?.length === 0 &&
        getIsFormValid()
      ) {
        profileCompleteInsightsEvent();
      }

      // If client survey disabled, set eligibility to valid
      if (!showClientSurvey && !profileProgressMap.eligibility.isDisabled) {
        handleHiddenEligibility();
      }
    }
  }, [incentiveSurveyAPICalled, networkPreferenceSurveyAPICalled]);

  const checkDisabled = (section: ProfileSection) => {
    if (section === 'enrollment' && !showEnrollment) {
      return true;
    }
    if (section === 'eligibility' && !showClientSurvey) {
      return true;
    }
    if (section === 'pregnancy' && (hidePregnancyQuestion || !canBearChild)) {
      return true;
    }
    if (section === 'hsaFunds' && (!is_hsa_funds_question_enabled || hasOopCost !== true)) {
      return true;
    }
    if (section === 'incentives' && !showIncentives) {
      return true;
    }
    if (section === 'providerPreferences' && !showProviderPreferences) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!checkDisabled('incentives') && profileProgressMap.incentives.isDisabled) {
      const tempProgressMap = { ...profileProgressMap };
      tempProgressMap.incentives.isDisabled = false;
      setProfileProgressMap(tempProgressMap);
    }
  }, [checkDisabled('incentives')]);

  // Sync both ways: if eligibility removes these questions, a still-enabled section
  // would block the results button with nothing left to answer.
  useEffect(() => {
    const isDisabled = checkDisabled('providerPreferences');

    // Must build on the latest map: on page load this runs in the same pass as the setup
    // and claims effects, and spreading this render's map would discard their updates.
    setProfileProgressMap((currentMap) => {
      const section = currentMap.providerPreferences;
      if (!section || section.isDisabled === isDisabled) {
        return currentMap;
      }
      return { ...currentMap, providerPreferences: { ...section, isDisabled } } as ProgressMap;
    });
  }, [checkDisabled('providerPreferences')]);

  const getSectionFromMap = (key: ProfileSection): ProgressSection => get(profileProgressMap, key);

  const getIsUnlocked = (key: ProfileSection): boolean => getSectionFromMap(key)?.isUnlocked || false;

  const getIsDirty = (key: ProfileSection): boolean => getSectionFromMap(key)?.isDirty || false;

  // return true if every enabled section is valid
  const getIsFormValid = () =>
    Object.values(profileProgressMap).every((section) => section.isValid || section.isDisabled);

  const getIsFormCompleted = () => isFormComplete;

  const markNotificationViewed = (key: ProfileSection) => {
    // We only want to get rid of the notification on viewed if it's for the good case
    // of a claimsSummary being viewed. If it is there because of a claimsError, we want
    // to leave it until the error is gone or the user skips the form on purpose.
    if (profileProgressMap[key]?.ctaNotification === CtaNotification.Success) {
      const updatedProgressMap = { ...profileProgressMap };
      updatedProgressMap[key].ctaNotification = null;
      setProfileProgressMap(updatedProgressMap);
    }
  };

  // when eligibility section is hidden either due to !showClientSurvey or client survey includes either only a dropdown with one option
  // or just the zipcode, set eligibility to valid and disabled.
  const handleHiddenEligibility = () => {
    handleUpdateProgress({
      key: 'eligibility',
      isValid: true,
      isDirty: false,
      isComplete: true,
    });
  };

  // called when inputs for a section is changed
  const handleSectionChange = ({
    key,
    isValid,
    isComplete,
  }: {
    key: ProfileSection;
    isValid: boolean;
    isComplete: boolean;
  }) => {
    if (!isEmpty(profileProgressMap)) {
      if (isValid) {
        handleUpdateProgress({
          key,
          isValid: true,
          isDirty: true,
          isComplete,
        });
      } else if (profileProgressMap[key].isValid) {
        // user action making section valid -> invalid
        handleUpdateProgress({
          key,
          isValid: false,
          isDirty: true,
          isComplete,
        });
      }
    }
  };
  // Determines if we should fire an additional /incentives request,
  // fired when changes to profile are made after initially completing all required fields
  useEffect(() => {
    if (!isEmpty(profileProgressMap)) {
      if (profileProgressMap.incentives.isUnlocked) {
        const progressMapWithoutIncentives = Object.values(
          omit(profileProgressMap, ['incentives', 'providerPreferences', 'enrollment', 'claims']),
        );
        const isAllFieldsValid = progressMapWithoutIncentives.every((i) => i.isValid || i.isDisabled);

        if (householdDataChanged) {
          if (isAllFieldsValid) {
            requestDebouncedHouseholdUpdate?.();
          } else {
            cancelDebouncedHouseholdUpdate?.();
          }
        }
      }
    }
    // Answers are a dependency because a section that is still incomplete never rewrites
    // profileProgressMap, and householdDataChanged is already true while an earlier save
    // is in flight. Without this, a second answer given during that save is never sent.
  }, [profileProgressMap, householdDataChanged, JSON.stringify(networkPreferenceSurveyAnswers)]);

  // determines if a section will be displayed
  const checkUnlocked = (section: ProfileSection, tempProgressMap: ProgressMap) => {
    let isUnlocked = false;

    if (isEmpty(tempProgressMap)) {
      return false;
    }

    switch (section) {
      case 'enrollment': {
        isUnlocked = showEnrollment;
        break;
      }
      case 'claims': {
        isUnlocked =
          !showEnrollment ||
          (showEnrollment && (tempProgressMap.enrollment?.isValid || tempProgressMap.eligibility?.isDirty));
        break;
      }
      case 'eligibility': {
        if (isClaimsFeatureEnabled) {
          isUnlocked = tempProgressMap.claims?.isComplete || tempProgressMap.eligibility?.isDirty;
        } else {
          isUnlocked =
            (!showEnrollment && showClientSurvey) ||
            (showEnrollment && (tempProgressMap.enrollment?.isValid || tempProgressMap.eligibility?.isDirty));
        }
        break;
      }
      case 'location': {
        // Determine if the eligibility section is effectively absent/hidden
        const eligibilityMissing = !tempProgressMap.eligibility || checkDisabled('eligibility');

        const claimsPresent =
          isClaimsFeatureEnabled && Boolean(tempProgressMap.claims) && !checkDisabled('claims');

        if (!eligibilityMissing) {
          // Normal flow: eligibility exists — same as current behaviour
          isUnlocked = tempProgressMap.eligibility?.isValid || tempProgressMap.location?.isDirty;
        } else if (showEnrollment) {
          // Eligibility missing → derive unlock from enrollment/claims
          if (claimsPresent) {
            isUnlocked = tempProgressMap.claims?.isComplete || tempProgressMap.location?.isDirty;
          } else {
            isUnlocked = tempProgressMap.enrollment?.isValid || tempProgressMap.location?.isDirty;
          }
        } else if (claimsPresent) {
          // No enrollment + no eligibility but claims exists
          isUnlocked = tempProgressMap.claims?.isComplete || tempProgressMap.location?.isDirty;
        } else {
          // No enrollment, no eligibility, no claims → fallback
          isUnlocked =
            !showClientSurvey || tempProgressMap.location?.isDirty || tempProgressMap.location?.isValid;
        }
        break;
      }
      case 'member': {
        isUnlocked = tempProgressMap.location?.isValid || tempProgressMap.member?.isDirty;
        break;
      }
      case 'pregnancy': {
        isUnlocked =
          canBearChild &&
          (tempProgressMap.member?.isValid || tempProgressMap.pregnancy?.isDirty) &&
          !hidePregnancyQuestion;
        break;
      }
      case 'income': {
        if (canBearChild) {
          if (
            tempProgressMap.pregnancy?.isValid ||
            hidePregnancyQuestion ||
            tempProgressMap.income?.isDirty
          ) {
            isUnlocked = true;
          }
        } else if (tempProgressMap.member?.isValid || tempProgressMap.income?.isDirty) {
          isUnlocked = true;
        }
        break;
      }
      case 'riskAssessment': {
        isUnlocked = tempProgressMap.income?.isValid || tempProgressMap.riskAssessment?.isDirty;
        break;
      }
      case 'capacityToPay': {
        isUnlocked = tempProgressMap.riskAssessment?.isValid || tempProgressMap.capacityToPay?.isDirty;
        break;
      }
      case 'hsaFunds': {
        isUnlocked = tempProgressMap.capacityToPay?.isValid || tempProgressMap.hsaFunds?.isDirty;
        break;
      }
      case 'incentives': {
        const { pregnancy, capacityToPay, hsaFunds, incentives } = tempProgressMap;

        // If HSA funds is disabled, use capacityToPay as the gating section
        // If HSA funds is enabled, use hsaFunds as the gating section
        const isHsaFundsDisabled = checkDisabled('hsaFunds');
        const gatingSectionValid = isHsaFundsDisabled ? capacityToPay?.isValid : hsaFunds?.isValid;

        isUnlocked =
          (pregnancy?.isComplete || !pregnancy?.isUnlocked) && (gatingSectionValid || incentives?.isDirty);
        break;
      }
      case 'providerPreferences': {
        // Ordering relative to Incentives is enforced by the display check in
        // ProfilePage, not here.
        const { pregnancy, capacityToPay, hsaFunds, providerPreferences } = tempProgressMap;

        const isHsaFundsDisabled = checkDisabled('hsaFunds');
        const gatingSectionValid = isHsaFundsDisabled ? capacityToPay?.isValid : hsaFunds?.isValid;

        isUnlocked =
          (pregnancy?.isComplete || !pregnancy?.isUnlocked) &&
          (gatingSectionValid || providerPreferences?.isDirty);
        break;
      }
      default: {
        break;
      }
    }

    return isUnlocked;
  };

  // This replaces UI-layer logic and keeps control inside ProfileContext.
  useEffect(() => {
    const locationSection = profileProgressMap.location;
    if (!locationSection) return;

    // If we already know validity, update progress accordingly.
    if (locationSection.isDirty) {
      handleUpdateProgress({
        key: 'location',
        isValid: locationSection.isValid,
        isDirty: true,
        isComplete: locationSection.isValid,
      });
    }
  }, [profileProgressMap.location?.isValid, profileProgressMap.location?.isDirty]);

  // Update isDisabled for pregnancy section based on changes to canBearChild.
  useEffect(() => {
    // APP-1345:
    // Disabling so we don't have the prefill issue with Conduent
    if (!is_profile_prefill_enabled) {
      const progressMapIsReady = !isEmpty(profileProgressMap);

      /**
       * Checking profileProgressMap.member.isComplete because if this block
       * is invoked too early, we'll wind up overwriting some necessary data in
       * the progress map. This is something that needs rework.
       */
      const readyToUpdate = !!profileProgressMap?.member?.isComplete;

      // Some customer configs disallow this section from the UX.
      const pregnancyQuestionEnabled = !hidePregnancyQuestion;

      const shouldUpdateProgress = isIntegrationPreFillEnabled
        ? progressMapIsReady && pregnancyQuestionEnabled && readyToUpdate
        : progressMapIsReady && pregnancyQuestionEnabled;

      if (shouldUpdateProgress) {
        const isValidAndComplete = survey.plan_child_question !== '';
        handleUpdateProgress({
          key: 'pregnancy',
          isValid: isValidAndComplete,
          isDirty: true,
          isComplete: isValidAndComplete,
        });
      }
    }
  }, [canBearChild, is_profile_prefill_enabled]);

  // update profileProgressMap after running validation on a section
  const handleUpdateProgress = ({
    key,
    isValid,
    isDirty,
    isComplete,
  }: {
    key: ProfileSection;
    isValid: boolean;
    isDirty: boolean;
    isComplete: boolean;
  }) => {
    setProfileProgressMap((profileProgressMap) => {
      // todo maybe use this to play audio/CC for given section?
      const sections = Object.keys(profileProgressMap) as ProfileSection[];
      const newProgressMap: ProgressMap = sections.reduce((acc, section: ProfileSection) => {
        const tempProgressMap = merge({}, profileProgressMap, acc);

        let isUnlocked = checkUnlocked(section, tempProgressMap);
        const isDisabled = checkDisabled(section);

        if (section === 'incentives' && key === 'member' && !isValid) {
          isUnlocked = false;
        }

        // unlocked/disabled for all sections, valid/dirty/complete for the section that invoked this function
        const updates =
          section === key
            ? { isValid, isDirty, isComplete, isUnlocked, isDisabled }
            : { isUnlocked, isDisabled };

        // for insights
        const isSectionCompleteForFirstTime =
          section === key && !completedSections.includes(key) && isComplete;
        if (isSectionCompleteForFirstTime) {
          setCompletedSections([...completedSections, key]);
        }
        return {
          ...tempProgressMap,
          [section]: {
            ...tempProgressMap[section],
            ...updates,
          },
        };
      }, {}) as ProgressMap;
      return newProgressMap;
    });
  };

  return (
    <ProfileContext.Provider
      value={{
        profileProgressMap,
        completedSections,
        getSectionFromMap,
        getIsUnlocked,
        getIsDirty,
        getIsFormValid,
        getIsFormCompleted,
        // TODO: Make this a private method.  We only want context updates to be handled with handleSectionChange
        handleUpdateProgress,
        handleSectionChange,
        handleHiddenEligibility,
        selectedPublicationInfo: publicationInfo,
        reviewMode,
        setReviewMode: handleReviewMode, // Provide setReviewMode
        tableViewEnabled,
        setTableViewEnabled: handleTableViewEnabled, // Provide setTableViewEnabled
        markNotificationViewed,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

const mapStateToProps = createStructuredSelector<GlobalReducerState, ProfileProviderStoreProps>({
  canBearChild: makeGetCanBearChild(),
  hidePregnancyQuestion: makeSelectConfigField('hide_pregnancy_question'),
  activePublicationInfo: makeSelectJvpField('active'),
  upcomingPublicationInfo: makeSelectJvpField('upcoming'),
  showClientSurvey: makeSelectShowClientSurvey(),
  survey: makeSelectSurvey(),
  incentiveSurvey: makeSelectProfileField('incentiveSurvey'),
  networkPreferenceSurvey: makeSelectProfileField('networkPreferenceSurvey'),
  selectedPublicationKey: makeGetSelectedPublicationKey(),
  incentiveSurveyAPICalled: makeSelectProfileField('incentiveSurveyAPICalled'),
  networkPreferenceSurveyAPICalled: makeSelectProfileField('networkPreferenceSurveyAPICalled'),
  networkPreferenceSurveyAnswers: makeSelectProfileField('networkPreferenceSurveyAnswers'),
  enrollmentEventId: makeGetSelectedEnrollmentEventId(),
  integratedUserPublicationKey: makeGetIntegratedUserPublicationKey(),
  claimsSummary: makeSelectProfileField('claimsSummary'),
  claimsError: makeSelectProfileField('claimsError'),
  claimsFormStatus: makeSelectProfileField('claimsFormStatus'),
  isClaimsFeatureEnabled: makeSelectIsClaimsFeatureEnabled(),
  hasOopCost: makeSelectHsaFundsOopCost(),
  householdDataChanged: makeSelectProfileField('householdDataChanged'),
});

const mapDispatchToProps = (dispatch: Dispatch): ProfileProviderDispatchProps => ({
  requestDebouncedHouseholdUpdate: () => dispatch(requestDebouncedHouseholdUpdate()),
  cancelDebouncedHouseholdUpdate: () => dispatch(cancelDebouncedHouseholdUpdate()),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(ProfileProvider);
